import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, resolveImage } from "@/lib/api";
import OmkarLogo from "@/components/OmkarLogo";
import { Button } from "@/components/ui/button";
import { 
  ShieldCheck, 
  CheckCircle2, 
  Phone, 
  Mail, 
  ChevronRight, 
  ArrowRight,
  Heart,
  Sun,
  Sparkles,
  Droplets,
  Store,
  UtensilsCrossed,
  Building2,
  Smile,
  Award
} from "lucide-react";

export default function LandingPage() {
  const { user } = useAuth();
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    api.get("/whitelabel")
      .then((res) => setBranding(res.data))
      .catch(() => {});
  }, []);

  const companyName = branding?.company_name || "Omkar Milk Distributor";
  const tagline = branding?.tagline || "Distributor";
  const logoUrl = branding?.logo_url ? resolveImage(branding.logo_url) : null;
  const phone = branding?.contact_phone || "+91 8010589178";
  const email = branding?.contact_email || "contact@omkarmilkdistributor.com";

  const emotionalPillars = [
    {
      icon: Heart,
      title: "Purity You Can Trust",
      desc: "For every Goa family starting their day with a warm cup of chai, we deliver pure, unadulterated milk crafted with love and care.",
      color: "text-rose-500",
      bg: "bg-rose-50 border-rose-100"
    },
    {
      icon: Sun,
      title: "Freshness Every Morning",
      desc: "Straight from dairy farms to your local store shelves before dawn break—preserving natural goodness, rich creaminess, and wholesome nutrition.",
      color: "text-amber-500",
      bg: "bg-amber-50 border-amber-100"
    },
    {
      icon: Droplets,
      title: "Direct Dairy Farm to Shop",
      desc: "We bridge the gap between dairy farms and local Goa stores, bringing farm-fresh milk, paneer, and curd straight to your neighborhood.",
      color: "text-blue-500",
      bg: "bg-blue-50 border-blue-100"
    },
    {
      icon: Smile,
      title: "Nourishing Goa Communities",
      desc: "More than just distribution—we build long-lasting relationships with local store owners, hotels, and families across Goa.",
      color: "text-emerald-500",
      bg: "bg-emerald-50 border-emerald-100"
    }
  ];

  const milkyProducts = [
    {
      title: "Pure Creamy Whole Milk",
      subtitle: "Farm-Fresh Goodness Every Day",
      desc: "Rich, wholesome cow & buffalo milk packed with natural calcium, proteins, and fresh taste. Perfect for daily tea, coffee, and family nutrition.",
      badge: "Pure & Unadulterated",
      icon: "🥛",
      tag: "Daily Essential"
    },
    {
      title: "Fresh Artisanal Paneer",
      subtitle: "Soft, Melt-in-Your-Mouth Slabs",
      desc: "Handcrafted fresh Paneer with zero preservatives. High in protein, creamy texture, perfect for home recipes, restaurants, and hotels.",
      badge: "100% Farm Fresh",
      icon: "🧈",
      tag: "High Protein"
    },
    {
      title: "Thick Creamy Dahi & Lassi",
      subtitle: "Traditional Homestyle Taste",
      desc: "Thick set curd and sweet, soothing lassi prepared from pure milk cultures. Healthy digestion for warm Goa mornings.",
      badge: "Active Cultures",
      icon: "🥣",
      tag: "Probiotic Rich"
    }
  ];

  const jsonLdData = {
    "@context": "https://schema.org",
    "@type": "WholesaleStore",
    "name": companyName,
    "image": logoUrl || "https://omkarmilkdistributor.com/logo.png",
    "@id": "https://omkarmilkdistributor.com/#organization",
    "url": "https://omkarmilkdistributor.com",
    "telephone": phone,
    "email": email,
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Panaji",
      "addressRegion": "Goa",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 15.4909,
      "longitude": 73.8278
    },
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Goa" },
      { "@type": "City", "name": "Panaji" },
      { "@type": "City", "name": "Margao" },
      { "@type": "City", "name": "Mapusa" },
      { "@type": "City", "name": "Vasco da Gama" },
      { "@type": "City", "name": "Calangute" },
      { "@type": "City", "name": "Candolim" }
    ],
    "description": "Premier wholesale dairy supplier and farm-fresh milk distributor in Goa. Supplying fresh cow milk, paneer, curd, and lassi directly to local stores, hotels, and restaurants.",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Wholesale Dairy Products Goa",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Pure Creamy Whole Milk",
            "description": "Farm fresh unadulterated cow & buffalo milk supplied to Goa stores."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Fresh Artisanal Paneer Slabs",
            "description": "Soft fresh Paneer slabs for hotels and restaurants in Goa."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Thick Creamy Dahi & Lassi",
            "description": "Probiotic set curd and sweet lassi for Goa retail stores."
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-sky-50/50 text-slate-800 flex flex-col font-sans selection:bg-blue-500 selection:text-white">
      {/* Search Engine & AI Structured Data (JSON-LD) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdData) }}
      />
      {/* Top Milky Announcement Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-sky-500 to-blue-600 text-white text-xs py-2.5 px-4 text-center font-medium flex items-center justify-center gap-2 border-b border-blue-400/30 shadow-sm">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] uppercase font-bold bg-amber-300 text-slate-900 tracking-wider shadow-sm">Farm Fresh</span>
        <span className="tracking-wide">✨ Delivering Pure, Fresh Dairy Goodness Direct from Dairy Farms to Goa Stores & Kitchens</span>
      </div>

      {/* Milky Fresh Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-blue-100 shadow-sm px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="h-10 max-w-[48px] object-contain shrink-0" />
            ) : (
              <OmkarLogo size={40} />
            )}
            <div>
              <div className="font-heading text-lg font-bold tracking-tight text-blue-950 leading-none">{companyName}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-blue-600 font-semibold mt-1">Fresh Dairy • Goa State</div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {user ? (
              <Button asChild variant="default" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 rounded-full shadow-md shadow-blue-600/20">
                <Link to="/dashboard">
                  Go to Dashboard <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            ) : (
              <Button asChild variant="default" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 rounded-full shadow-md shadow-blue-600/20">
                <Link to="/login">
                  Store Partner Portal <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section — Milky Theme */}
      <section className="relative py-20 lg:py-28 overflow-hidden bg-gradient-to-b from-white via-sky-50/80 to-blue-50/60 border-b border-blue-100">
        {/* Soft Background Accents */}
        <div className="absolute top-10 left-10 w-96 h-96 bg-blue-200/40 rounded-full filter blur-3xl opacity-60 pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-sky-200/40 rounded-full filter blur-3xl opacity-60 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 border border-blue-200 text-blue-700 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="size-3.5 text-blue-600" /> Pure • Fresh • Nourishing
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-slate-900 tracking-tight leading-[1.12]">
              Fresh Dairy Filled with <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-sky-500 to-indigo-600">Love, Purity & Care</span> for Goa
            </h1>

            <p className="text-slate-600 text-base sm:text-lg max-w-2xl leading-relaxed">
              We bring the wholesome warmth of fresh dairy directly from farms to neighborhood shops, stores, hotels, and homes across Goa. Pure, unadulterated milk and dairy products delivered fresh every single morning.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 rounded-full shadow-lg shadow-blue-600/25">
                <Link to={user ? "/dashboard" : "/login"}>
                  Partner Store Sign In <ChevronRight className="ml-2 size-4" />
                </Link>
              </Button>
              <a href={`tel:${phone}`} className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full border border-blue-200 bg-white hover:bg-blue-50 text-slate-700 text-sm font-medium transition shadow-sm">
                <Phone className="size-4 text-blue-600" /> {phone}
              </a>
            </div>

            {/* Emotional Highlights */}
            <div className="pt-8 grid grid-cols-3 gap-6 border-t border-blue-100">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-full bg-blue-100 text-blue-600">
                  <Droplets className="size-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900 font-heading">100% Pure</div>
                  <div className="text-xs text-slate-500">Farm Fresh Milk</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-full bg-amber-100 text-amber-600">
                  <Sun className="size-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900 font-heading">Every Morning</div>
                  <div className="text-xs text-slate-500">Fresh Delivery</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-full bg-rose-100 text-rose-600">
                  <Heart className="size-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900 font-heading">Loved in Goa</div>
                  <div className="text-xs text-slate-500">Family & Shops</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            {/* Milky Card Illustration Container */}
            <div className="relative rounded-3xl border border-blue-100 bg-white/90 p-7 shadow-xl shadow-blue-900/5 backdrop-blur-xl space-y-6">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <span className="font-heading font-bold text-slate-900 flex items-center gap-2 text-sm">
                  <Award className="size-5 text-blue-600" /> Pure Farm-to-Table Quality
                </span>
                <span className="text-xs text-blue-700 bg-blue-50 px-3 py-1 rounded-full border border-blue-200 font-bold">100% Fresh</span>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-blue-50/50 border border-blue-100">
                  <div className="text-3xl">🐄</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Direct From Dairy Farmers</h4>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">Sourced from healthy, well-cared-for cows and buffaloes with high natural nutrition.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-sky-50/50 border border-sky-100">
                  <div className="text-3xl">🏪</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Direct To Local Goa Stores</h4>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">We deliver directly to your neighborhood stores so families get peak freshness every day.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-amber-50/50 border border-amber-100">
                  <div className="text-3xl">☕</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Every Family's Morning Smile</h4>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">Fueling Goa’s morning tea, breakfast, hotel dining, and sweet creations with pure dairy.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-600 to-sky-600 text-white p-4.5 rounded-2xl text-center shadow-md">
                <p className="text-xs text-blue-100 font-medium">Are you a Retailer, Store Owner, Hotel, or Restaurant in Goa?</p>
                <Link to="/login" className="text-sm font-bold text-white hover:text-amber-200 inline-flex items-center gap-1.5 mt-2 transition">
                  Join our Goa Partner Network <ArrowRight className="size-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emotional Pillars Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-xs uppercase tracking-[0.25em] text-blue-600 font-bold">Our Promise & Passion</h2>
            <p className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mt-2">More Than Dairy — It's About Family & Care</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {emotionalPillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <div key={idx} className={`p-7 rounded-2xl border ${pillar.bg} transition hover:shadow-md space-y-4`}>
                  <div className={`p-3 w-fit rounded-xl bg-white shadow-sm ${pillar.color}`}>
                    <Icon className="size-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 font-heading">{pillar.title}</h3>
                  <p className="text-slate-600 text-xs leading-relaxed">{pillar.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Showcase Section — Milky White Design */}
      <section className="py-20 bg-gradient-to-b from-blue-50/40 via-white to-sky-50/50 border-y border-blue-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-xs uppercase tracking-[0.25em] text-blue-600 font-bold">Farm-Fresh Range</h2>
            <p className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mt-2">Wholesome Dairy Delivered Fresh to Goa</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {milkyProducts.map((prod, idx) => (
              <div key={idx} className="bg-white border border-blue-100 rounded-3xl p-6 shadow-sm hover:shadow-md hover:border-blue-300 transition flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-4xl">{prod.icon}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
                      {prod.tag}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 font-heading">{prod.title}</h3>
                  <div className="text-xs font-semibold text-blue-600 mt-1">{prod.subtitle}</div>
                  <p className="text-slate-600 text-xs mt-3 leading-relaxed">{prod.desc}</p>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-bold text-emerald-700">
                  <CheckCircle2 className="size-4 text-emerald-600 shrink-0" />
                  <span>{prod.badge}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Direct Supply Network */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider mb-3">
              <Store className="size-3.5" /> Direct Store & Kitchen Supply
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 tracking-tight">
              Supplying Goa's Preferred Stores, Hotels & Restaurants
            </h2>
            <p className="text-slate-600 text-sm mt-3 leading-relaxed">
              We proudly supply fresh milk and dairy directly to local grocery stores, supermarkets, beach resorts, fine dining kitchens, and tea outlets throughout Goa.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            <div className="bg-sky-50/50 p-8 rounded-3xl border border-sky-100 text-center space-y-4 shadow-sm hover:shadow-md transition">
              <div className="p-4 rounded-2xl bg-white text-blue-600 w-fit mx-auto shadow-sm">
                <Store className="size-8" />
              </div>
              <h4 className="font-bold text-slate-900 font-heading text-xl">Local Stores & Milk Depots</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Keeping neighborhood shelves stocked every morning with pure milk pouches, dahi, and butter for local Goa families.
              </p>
            </div>

            <div className="bg-amber-50/50 p-8 rounded-3xl border border-amber-100 text-center space-y-4 shadow-sm hover:shadow-md transition">
              <div className="p-4 rounded-2xl bg-white text-amber-600 w-fit mx-auto shadow-sm">
                <UtensilsCrossed className="size-8" />
              </div>
              <h4 className="font-bold text-slate-900 font-heading text-xl">Restaurants & Sweet Shops</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Fresh Paneer slabs, thick curd, and rich Ghee delivered straight to kitchens for authentic recipes and sweet delicacies.
              </p>
            </div>

            <div className="bg-blue-50/50 p-8 rounded-3xl border border-blue-100 text-center space-y-4 shadow-sm hover:shadow-md transition">
              <div className="p-4 rounded-2xl bg-white text-blue-700 w-fit mx-auto shadow-sm">
                <Building2 className="size-8" />
              </div>
              <h4 className="font-bold text-slate-900 font-heading text-xl">Hotels & Beach Resorts</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Bulk daily milk and butter deliveries ensuring guest breakfast buffets and culinary teams receive top-tier dairy quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Partner Section */}
      <section className="py-16 bg-gradient-to-r from-blue-700 via-sky-600 to-blue-700 text-white text-center px-6 shadow-xl">
        <div className="max-w-3xl mx-auto space-y-6">
          <h2 className="text-3xl sm:text-4xl font-bold font-heading text-white">Bring Pure Farm-Fresh Dairy to Your Store in Goa</h2>
          <p className="text-blue-100 text-sm leading-relaxed">
            Join hundreds of trusted Goa retailers, hotels, and restaurants. Enjoy direct farm supply, friendly service, custom store pricing, and guaranteed morning fresh delivery.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Button asChild size="lg" className="bg-white hover:bg-blue-50 text-blue-700 font-bold px-8 rounded-full shadow-lg">
              <Link to={user ? "/dashboard" : "/login"}>
                Partner Store Login <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
            <a href={`tel:${phone}`} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-blue-300/60 bg-blue-800/40 hover:bg-blue-800/60 text-white text-sm font-medium transition">
              <Phone className="size-4 text-amber-300" /> Call Dairy Desk ({phone})
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto bg-white border-t border-blue-100 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <OmkarLogo size={32} />
            <div>
              <span className="font-bold text-slate-900 font-heading">{companyName}</span> — Delivering Pure Dairy Goodness in Goa
            </div>
          </div>

          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5"><Phone className="size-3.5 text-blue-600" /> {phone}</span>
            <span className="flex items-center gap-1.5"><Mail className="size-3.5 text-blue-600" /> {email}</span>
          </div>

          <div>
            © {new Date().getFullYear()} {companyName}. All Rights Reserved. Pure Fresh Dairy for Goa.
          </div>
        </div>
      </footer>
    </div>
  );
}
